import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import RichTextEditor from '../ui/RichTextEditor';
import { 
  ArrowLeft, 
  Save, 
  Plus,
  X,
  GripVertical,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import '../../styles/CollectionDetailAdmin.css';
import '../../styles/NewSubmission.css';

const OrganizationTemplateManager = () => {
  const { selectedOrganization } = useOutletContext() || {};
  const navigate = useNavigate();
  
  const [template, setTemplate] = useState(null);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [autoSaveTimeout, setAutoSaveTimeout] = useState(null);

  useEffect(() => {
    if (selectedOrganization?.organization_id) {
      loadTemplate();
    }
  }, [selectedOrganization?.organization_id]);

  // Auto-save fields after 2 seconds of no changes
  useEffect(() => {
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }
    
    if (fields.length > 0 && template) {
      const timeout = setTimeout(() => {
        handleSave(true); // Pass true to indicate auto-save
      }, 2000);
      
      setAutoSaveTimeout(timeout);
    }
    
    return () => {
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }
    };
  }, [fields, template]); // Added template to dependencies

  const loadTemplate = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('organization_submission_templates')
        .select('*')
        .eq('organization_id', selectedOrganization.organization_id)
        .eq('is_default', true)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 = not found
        throw fetchError;
      }

      if (data) {
        setTemplate(data);
        // Parse field_definitions
        const fieldDefs = typeof data.field_definitions === 'string' 
          ? JSON.parse(data.field_definitions) 
          : data.field_definitions;
        setFields(Array.isArray(fieldDefs) ? fieldDefs : []);
      } else {
        // No template exists yet - create empty one
        setFields([]);
      }
    } catch (err) {
      console.error('Error loading template:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      type: 'text',
      label: 'New Field',
      value: '',
      required: true,
      placeholder: ''
    };
    setFields([...fields, newField]);
  };

  const removeField = (index) => {
    const fieldLabel = fields[index]?.label || `Field ${index + 1}`;
    if (!window.confirm(`Are you sure you want to delete "${fieldLabel}"? This action cannot be undone.`)) {
      return;
    }
    setFields(fields.filter((_, i) => i !== index));
  };

  const updateField = (index, property, value) => {
    const updatedFields = fields.map((field, i) => 
      i === index ? { ...field, [property]: value } : field
    );
    setFields(updatedFields);
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newFields = [...fields];
    const draggedField = newFields[draggedIndex];
    
    // Remove from old position
    newFields.splice(draggedIndex, 1);
    // Insert at new position
    newFields.splice(index, 0, draggedField);
    
    setFields(newFields);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSave = async (isAutoSave = false) => {
    try {
      setSaving(true);
      setError(null);

      if (!selectedOrganization?.organization_id) {
        throw new Error('No organization selected');
      }

      const templateData = {
        organization_id: selectedOrganization.organization_id,
        template_name: 'Default Submission Template',
        field_definitions: fields,
        is_default: true
      };

      if (template?.id) {
        // Update existing template
        const { error: updateError } = await supabase
          .from('organization_submission_templates')
          .update(templateData)
          .eq('id', template.id);

        if (updateError) throw updateError;
      } else {
        // Create new template
        const { data: newTemplate, error: insertError } = await supabase
          .from('organization_submission_templates')
          .insert([templateData])
          .select()
          .single();

        if (insertError) throw insertError;
        
        // Set the template state so next save is an update
        if (newTemplate) {
          setTemplate(newTemplate);
        }
      }

      if (!isAutoSave) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
      
    } catch (err) {
      console.error('Error saving template:', err);
      if (!isAutoSave) {
        setError(err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      if (dateString.includes('T')) {
        const [datePart, timePart] = dateString.split('T');
        const [time] = timePart.split('.');
        return `${datePart}T${time.substring(0, 5)}`;
      } else {
        const localDate = new Date(dateString + 'T00:00:00');
        if (isNaN(localDate.getTime())) return '';
        
        const year = localDate.getFullYear();
        const month = String(localDate.getMonth() + 1).padStart(2, '0');
        const day = String(localDate.getDate()).padStart(2, '0');
        
        return `${year}-${month}-${day}T23:59`;
      }
    } catch (e) {
      console.warn('Invalid date format:', dateString);
      return '';
    }
  };

  if (loading) {
    return (
      <div className="collection-detail-admin">
        <div className="loading-state">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <h3>Loading Template...</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="collection-detail-admin">
      {/* Header */}
      <div className="detail-header">
        <button onClick={() => navigate(-1)} className="back-button">
          <ArrowLeft size={20} />
          Back
        </button>
        
        <div className="header-content">
          <h1>Submission Template</h1>
          <p>Configure default fields for {selectedOrganization?.name || 'your organization'}</p>
        </div>
        
        <div className="header-actions">
          {success && (
            <div className="success-indicator">
              <CheckCircle size={20} />
              Saved!
            </div>
          )}
          
          <button 
            onClick={handleSave}
            disabled={saving}
            className="btn-primary"
          >
            <Save size={16} />
            {saving ? 'Saving...' : 'Save Template'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Template Form */}
      <div className="detail-content">
        <div className="form-section">
          <h2>Template Fields</h2>
          <p className="section-description">
            Define the fields that will appear in all submissions for this organization. 
            Drag to reorder in the list below.
          </p>
          
          {/* Compact field list for easy reordering */}
          {fields.length > 0 && (
            <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#6b7280' }}>FIELD ORDER</h3>
              <div>
                {fields.map((field, index) => (
                  <div 
                    key={field.id || index}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      marginBottom: '4px',
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      cursor: 'move',
                      opacity: draggedIndex === index ? 0.5 : 1,
                      transition: 'opacity 0.2s'
                    }}
                  >
                    <GripVertical size={16} color="#9ca3af" />
                    <span style={{ fontSize: '14px', fontWeight: '500', flex: 1 }}>
                      {field.label || 'Untitled Field'}
                    </span>
                    <span style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>
                      {field.type || 'text'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <button 
            type="button" 
            onClick={addField}
            className="btn-secondary add-section-btn"
            style={{ marginBottom: '24px' }}
          >
            <Plus size={16} />
            Add Field
          </button>
          
          <div className="form-sections-container">
            {fields.map((field, index) => (
              <div 
                key={field.id || index}
                className="form-section-item"
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                style={{ 
                  opacity: draggedIndex === index ? 0.5 : 1,
                  cursor: 'move'
                }}
              >
                {/* Field Title - Large, updates as you type */}
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <input
                    type="text"
                    value={field.label || ''}
                    onChange={(e) => updateField(index, 'label', e.target.value)}
                    placeholder="Field Title"
                    style={{ 
                      fontSize: '20px', 
                      fontWeight: '600',
                      border: 'none',
                      borderBottom: '2px solid #e5e7eb',
                      padding: '8px 0',
                      width: '100%'
                    }}
                  />
                </div>

                <div className="form-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <div className="form-group" style={{ margin: 0, flex: 1 }}>
                    <select
                      value={field.type || 'text'}
                      onChange={(e) => updateField(index, 'type', e.target.value)}
                      style={{ width: '200px' }}
                    >
                      <option value="text">Text Input</option>
                      <option value="textarea">Text Area</option>
                      <option value="html">Rich Text/HTML</option>
                      <option value="date">Date</option>
                      <option value="datetime">Date & Time</option>
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="number">Number</option>
                      <option value="select">Dropdown</option>
                      <option value="checkbox">Checkbox</option>
                      <option value="radio">Radio Buttons</option>
                    </select>
                  </div>
                  <button 
                    type="button" 
                    onClick={() => removeField(index)}
                    className="btn-danger-small"
                    title="Delete field"
                  >
                    <X size={16} />
                  </button>
                </div>
                
                {/* Date field */}
                {field.type === 'date' && (
                  <div className="form-group">
                    <label>Default Date Value</label>
                    <input
                      type="date"
                      value={field.value || ''}
                      onChange={(e) => updateField(index, 'value', e.target.value)}
                    />
                  </div>
                )}
                
                {/* Datetime field */}
                {field.type === 'datetime' && (
                  <div className="form-group">
                    <label>Default Date & Time Value</label>
                    <input
                      type="datetime-local"
                      value={field.value ? formatDateForInput(field.value) : ''}
                      onChange={(e) => {
                        const localDate = new Date(e.target.value);
                        const isoString = localDate.toISOString();
                        updateField(index, 'value', isoString);
                      }}
                    />
                    <small className="field-note">Stored as: {field.value || 'Not set'}</small>
                  </div>
                )}
                
                {/* HTML/Rich text field */}
                {field.type === 'html' && (
                  <div className="form-group">
                    <label>Default HTML Content</label>
                    <RichTextEditor
                      value={field.value || ''}
                      onChange={(value) => updateField(index, 'value', value)}
                      placeholder="Enter default rich text content..."
                      minHeight="150px"
                    />
                  </div>
                )}
                
                {/* Standard placeholder for text-based fields */}
                {!['date', 'datetime', 'html', 'select', 'radio', 'checkbox'].includes(field.type) && (
                  <div className="form-group">
                    <label>Placeholder Text</label>
                    <input
                      type="text"
                      value={field.placeholder || ''}
                      onChange={(e) => updateField(index, 'placeholder', e.target.value)}
                      placeholder="Placeholder text"
                    />
                  </div>
                )}
                
                {/* Options for select/radio */}
                {(field.type === 'select' || field.type === 'radio') && (
                  <div className="form-group">
                    <label>Options (one per line)</label>
                    <textarea
                      value={field.options ? field.options.join('\n') : ''}
                      onChange={(e) => updateField(index, 'options', e.target.value.split('\n').filter(opt => opt.trim()))}
                      placeholder="Option 1&#10;Option 2&#10;Option 3"
                      rows={4}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationTemplateManager;
